// Client for /api/predict-config -- the grey pre-fill on a New Config.
//
// Everything here is best-effort and bounded: a prediction is a convenience, so a slow or
// failing call must never hold the form hostage. On any failure the form simply opens the way
// it always did.

import type { AnswerState } from "../types";
import type { MirrorPlan, PredictionInput, PredictionSource } from "../state/AnswersContext";

const API = "/api/predict-config";

export type PredictionResponse = {
  found: boolean;
  basis?: "customer" | "everyone";
  source?: { id: string; name: string; customerName: string | null; createdAt: string; kind?: "cpq2" | "legacy" | "mirror" };
  /** A cpq2 configuration, whole. */
  configSnapshot?: string;
  /** Or a legacy configuration, translated into cpq2 answers -- or, with `mirror`, the
   *  snapshot's answers mirrored for the other burner of an MB. */
  answers?: AnswerState;
  mirror?: MirrorPlan;
};

async function withTimeout(url: string, init: RequestInit, ms: number): Promise<Response | null> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: ctl.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

async function read(res: Response | null): Promise<PredictionResponse | null> {
  if (!res || !res.ok) return null;
  try {
    const body = (await res.json()) as PredictionResponse;
    return body && body.found && body.source && (body.configSnapshot || body.answers) ? body : null;
  } catch {
    return null;
  }
}

/** The configuration to pre-fill a New Config with, or null. The transaction lets the server
 *  find an MB burner already configured on it (api/shared/predict/siblings.js). */
export async function fetchLaunchPrediction(
  product: string,
  customerId: string | null,
  transactionId: string | null
): Promise<PredictionResponse | null> {
  const q = new URLSearchParams({ product });
  if (customerId) q.set("customerId", customerId);
  if (transactionId) q.set("transactionId", transactionId);
  return read(await withTimeout(`${API}?${q}`, { headers: { Accept: "application/json" } }, 6000));
}

/** What the field-by-field prediction predicts from (api/predict-config stage "evidence"): past
 *  configurations -- a cpq2 snapshot, or a legacy order's chosen answers -- and which of them
 *  the server would have picked whole. `mirror` instead for an MB housing's opposite side. */
export type EvidenceSource = {
  id: string; name: string; kind: "cpq2" | "legacy"; createdAt: number; customerId: string; user: string; auto: boolean; quote?: boolean;
  configSnapshot?: string; flat?: Record<string, string>;
};
export type EvidenceResponse = {
  found: boolean; sources: EvidenceSource[]; pickId: string | null; mirror?: PredictionResponse;
  /** The part the order's own quote names (api/shared/predict/siblings.js quoteNamedPart): a
   *  repeat of it, whole, housing included. */
  named?: PredictionResponse;
};

export async function fetchEvidence(
  product: string,
  customerId: string | null,
  transactionId: string | null,
  user: string | null
): Promise<EvidenceResponse | null> {
  const q = new URLSearchParams({ product, stage: "evidence" });
  if (customerId) q.set("customerId", customerId);
  if (transactionId) q.set("transactionId", transactionId);
  if (user) q.set("user", user);
  const res = await withTimeout(`${API}?${q}`, { headers: { Accept: "application/json" } }, 8000);
  if (!res || !res.ok) return null;
  try {
    const body = (await res.json()) as EvidenceResponse;
    if (body && body.mirror && read2(body.mirror)) return { found: true, sources: [], pickId: null, mirror: body.mirror };
    if (!body || !Array.isArray(body.sources)) return null;
    const named = body.named && read2(body.named) ? body.named : undefined;
    return { found: !!body.found, sources: body.sources, pickId: body.pickId ?? null, ...(named ? { named } : {}) };
  } catch {
    return null;
  }
}
const read2 = (b: PredictionResponse) => !!(b && b.found && b.source && (b.configSnapshot || b.answers));

/** The PO as the server read it: one entry per burner line, as cpq2 answers. */
export type PoEvidence = {
  found: boolean;
  poNumber?: string | null;
  fileId?: string;
  fileName?: string;
  /** "name": the file is named for the PO number; "text": found by its text (po-read.js). */
  foundBy?: string;
  /** This customer's record per answer, [right, seen] (po-profile.js). */
  trust?: Record<string, [number, number]>;
  /** The Flameco quotes the PO cites, as evidence ids. */
  quotes?: { id: string; tranid: string }[];
  /** The customer's earlier order the PO says this one repeats ("repeat of PO 70797"), as
   *  evidence ids, newest first. */
  repeats?: { id: string; tranid: string; po?: string }[];
  repeatOf?: string | null;
  lines?: { index: number; model: string | null; text: string; answers: AnswerState; notes?: { key: string; kind: string; text: string }[] }[];
};

/** At Save & Submit of a configuration the PO filled: per answer, what the PO said and what was
 *  submitted (api/predict-config stage "po-feedback"). The customer's profile learns from it. */
export function logPoFeedback(body: { customerId: string | null; transactionId: string | null; fileId: string; poNumber: string; lineIndex: number; itemName?: string | null; userName?: string | null; pairs: { key: string; po: string; final: string | null }[] }): void {
  try {
    void fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ stage: "po-feedback", ...body }), keepalive: true }).catch(() => {});
  } catch {
    /* feedback is best-effort */
  }
}
/** The customer's PO on this sales order, read into answers (api/predict-config stage "po").
 *  Asked for beside the launch and never awaited by it: uncached it takes seconds. */
export async function fetchPo(product: string, transactionId: string | null): Promise<PoEvidence | null> {
  if (!transactionId) return null;
  const q = new URLSearchParams({ product, stage: "po", transactionId });
  const res = await withTimeout(`${API}?${q}`, { headers: { Accept: "application/json" } }, 40000);
  if (!res || !res.ok) return null;
  try {
    const body = (await res.json()) as PoEvidence;
    return body && body.found && Array.isArray(body.lines) ? body : null;
  } catch {
    return null;
  }
}

/** One entry of the Recent menu: a whole earlier configuration of this customer. */
export type RecentConfig = Pick<PredictionResponse, "source" | "configSnapshot" | "answers">;

/** The customer's most recent distinct configurations, newest first ([] on any failure). */
const recentCache = new Map<string, Promise<RecentConfig[]>>();
export function fetchRecentConfigs(product: string, customerId: string | null): Promise<RecentConfig[]> {
  if (!customerId) return Promise.resolve([]);
  const key = `${product}|${customerId}`;
  let p = recentCache.get(key);
  if (!p) { p = fetchRecentConfigsNow(product, customerId); recentCache.set(key, p); }
  return p;
}
async function fetchRecentConfigsNow(product: string, customerId: string): Promise<RecentConfig[]> {
  const q = new URLSearchParams({ product, stage: "recent", customerId });
  const res = await withTimeout(`${API}?${q}`, { headers: { Accept: "application/json" } }, 8000);
  if (!res || !res.ok) return [];
  try {
    const body = (await res.json()) as { recent?: RecentConfig[] };
    return Array.isArray(body.recent) ? body.recent.filter((r) => r && r.source && (r.configSnapshot || r.answers)) : [];
  } catch {
    return [];
  }
}

/** A better configuration for everything after the first page, given the first page as it stands. */
export async function fetchRefinePrediction(
  product: string,
  customerId: string | null,
  pageAnswers: AnswerState,
  changedKeys: string[]
): Promise<PredictionResponse | null> {
  return read(
    await withTimeout(
      API,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        // changedKeys: what the user changed from the prediction -- the real news (cpq2.js).
        body: JSON.stringify({ stage: "refine", product, customerId, pageAnswers, changedKeys }),
      },
      5000
    )
  );
}

export function toSource(r: PredictionResponse): PredictionSource {
  return {
    id: r.source!.id,
    name: r.source!.name,
    customerName: r.source!.customerName,
    createdAt: r.source!.createdAt,
    basis: r.basis === "customer" ? "customer" : "everyone",
    kind: r.source!.kind === "legacy" || r.source!.kind === "mirror" ? r.source!.kind : "cpq2",
  };
}

/** What to apply: the snapshot (with the mirrored answers, for an MB burner), or the
 *  translated legacy answers. */
export function toInput(r: PredictionResponse): PredictionInput {
  if (r.configSnapshot) return r.mirror && r.answers ? { configSnapshot: r.configSnapshot, answers: r.answers } : { configSnapshot: r.configSnapshot };
  return { answers: r.answers ?? {} };
}

export type PredictionOutcome = {
  product: string;
  customerId: string | null;
  userId: string | null;
  userName: string | null;
  originType: string | null;
  originTranid: string | null;
  itemName: string | null;
  launchSourceId: string | null;
  refineSourceId: string | null;
  predictionOn: boolean;
  predictedAnswers: number;
  changedAnswers: number;
  changedKeys: string[];
  /** Per changed answer: what was predicted and what was configured (the comparison Kevin
   *  asked for, 2026-10-19, now that the prediction runs in the background by default). */
  changedDetail?: Array<{ key: string; predicted: string; final: string; from?: string }>;
  /** How many answers each source gave, by the source's words (the hover text without its hit
   *  rate), so the live hit rate of every source can be worked out from the changed ones. */
  predictedBySource?: Record<string, number>;
};

/** The opposite side of the MB housing just saved (api/shared/predict/siblings.js
 *  mirrorOfSaved), as a launch response -- or null: not a housing with a hand, its opposite is
 *  already done, or the call failed. */
export async function fetchOppositeSide(
  product: string,
  customerId: string | null,
  saved: { transactionId: string | null; itemId: string; name: string; configSnapshot: string }
): Promise<PredictionResponse | null> {
  return read(
    await withTimeout(
      API,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ stage: "mirror", product, customerId, ...saved }),
      },
      4000
    )
  );
}

/** Record how the prediction did. Fire-and-forget: never delays or fails a submit. */
export function logPredictionOutcome(o: PredictionOutcome): void {
  void withTimeout(
    API,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stage: "outcome", ...o }),
      keepalive: true,
    },
    8000
  );
}
